import { 
  Shield, Building2, TrendingUp, Users, BarChart3, Globe, 
  Database, Zap, CheckCircle, Lock, Clock, Settings,
  FileText, Search, AlertTriangle, Calculator, Target,
  Truck, Package, Clipboard, Stethoscope, Scale,
  ShoppingCart, ChefHat, Bed, DollarSign, Activity,
  Car, Wrench, CreditCard, Phone, MessageSquare
} from 'lucide-react';
import { ProductConfigs } from './types';

export const industryVerticalsSolutionsConfigs: ProductConfigs = {
  'fieldwire-construction': {
    keyBenefits: [
      {
        icon: Building2,
        title: 'Construction Project Management',
        description: 'Comprehensive project oversight with real-time progress tracking, milestone management, and automated reporting for construction teams.',
        metrics: '45% faster project completion',
        color: 'from-orange-500 to-red-500'
      },
      {
        icon: Shield,
        title: 'Safety Compliance Automation',
        description: 'Automated safety inspections, incident reporting, and regulatory compliance tracking to ensure jobsite safety and reduce liability.',
        metrics: '60% reduction in safety incidents',
        color: 'from-green-500 to-emerald-500'
      },
      {
        icon: Users,
        title: 'Mobile Workforce Coordination',
        description: 'Real-time crew coordination, task assignments, and field communication tools optimized for construction site workflows.',
        metrics: '35% improvement in workforce efficiency',
        color: 'from-blue-500 to-indigo-500'
      },
      {
        icon: FileText,
        title: 'Document & Blueprint Management',
        description: 'Centralized document storage with version control, blueprint markup, and instant field access to critical project documents.',
        metrics: '50% faster document retrieval',
        color: 'from-purple-500 to-violet-500'
      },
      {
        icon: BarChart3,
        title: 'Construction Analytics & Reporting',
        description: 'Advanced project analytics with cost tracking, productivity metrics, and predictive insights for better decision-making.',
        metrics: '25% cost savings on projects',
        color: 'from-teal-500 to-cyan-500'
      },
      {
        icon: Calculator,
        title: 'Resource & Cost Optimization',
        description: 'Intelligent resource allocation, material tracking, and cost optimization tools to maximize project profitability.',
        metrics: '30% reduction in material waste',
        color: 'from-yellow-500 to-amber-500'
      }
    ],
    advancedCapabilities: [
      {
        icon: Target,
        title: 'AI-Powered Project Planning',
        description: 'Machine learning algorithms optimize project schedules, resource allocation, and risk assessment for construction projects.',
        metrics: 'Predictive accuracy up to 92%',
        color: 'from-rose-500 to-pink-500'
      },
      {
        icon: Globe,
        title: 'IoT Construction Site Monitoring',
        description: 'Integrated IoT sensors for equipment tracking, environmental monitoring, and automated progress documentation.',
        metrics: 'Real-time monitoring of 500+ data points',
        color: 'from-indigo-500 to-purple-500'
      },
      {
        icon: TrendingUp,
        title: 'Predictive Maintenance Analytics',
        description: 'AI-driven equipment maintenance scheduling and failure prediction to minimize downtime and repair costs.',
        metrics: '40% reduction in equipment downtime',
        color: 'from-green-500 to-teal-500'
      },
      {
        icon: Database,
        title: 'Construction Data Intelligence',
        description: 'Comprehensive data analytics platform aggregating project metrics, workforce performance, and industry benchmarks.',
        metrics: 'Process 1M+ construction data points daily',
        color: 'from-blue-500 to-cyan-500'
      },
      {
        icon: Settings,
        title: 'Enterprise Construction Integrations',
        description: 'Seamless integration with BIM software, accounting systems, and construction management platforms.',
        metrics: '200+ construction tool integrations',
        color: 'from-orange-500 to-red-500'
      },
      {
        icon: Activity,
        title: 'Real-Time Quality Control',
        description: 'Automated quality inspections, defect tracking, and compliance verification with mobile documentation tools.',
        metrics: '55% improvement in quality scores',
        color: 'from-purple-500 to-indigo-500'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with regular third-party security audits and certifications.',
        metrics: '99.9% compliance uptime',
        color: 'from-blue-600 to-indigo-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat detection and analysis with ML-powered anomaly detection and automated incident response.',
        metrics: '<5ms threat detection',
        color: 'from-green-600 to-emerald-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling, load balancing, and 99.99% uptime guarantee.',
        metrics: '10M+ secure transactions daily',
        color: 'from-purple-600 to-violet-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered firewall with advanced DDoS protection, rate limiting, and geo-blocking capabilities.',
        metrics: 'Blocks 99.9% of malicious traffic',
        color: 'from-orange-600 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO, biometric authentication, hardware tokens, and adaptive authentication policies.',
        metrics: '99.95% authentication success rate',
        color: 'from-teal-600 to-cyan-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning, behavioral analysis, and automated threat response capabilities.',
        metrics: 'Monitor 1B+ security events daily',
        color: 'from-indigo-600 to-blue-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated DLP policies, encrypted backups, point-in-time recovery, and cross-region data replication.',
        metrics: '15-minute recovery point objective',
        color: 'from-red-600 to-pink-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Zero-trust network architecture with micro-segmentation, continuous monitoring, and network analytics.',
        metrics: '100% network traffic visibility',
        color: 'from-emerald-600 to-green-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: '24/7 vulnerability scanning, penetration testing, security assessments, and automated patch management.',
        metrics: 'Sub-hour vulnerability detection',
        color: 'from-violet-600 to-purple-600'
      }
    ]
  },

  'automotive-dealership-platform': {
    keyBenefits: [
      {
        icon: Car,
        title: 'Vehicle Inventory Management',
        description: 'Real-time inventory tracking across new, used, and certified pre-owned vehicles with VIN-level detail, photo management, and multi-location stock visibility.',
        metrics: '40% faster inventory turnover',
        color: 'from-red-500 to-rose-600'
      },
      {
        icon: Users,
        title: 'Dealership CRM & Lead Management',
        description: 'Unified customer profiles, automated lead routing, showroom traffic tracking, and intelligent follow-up sequences across every sales channel.',
        metrics: '3x lead-to-sale conversion lift',
        color: 'from-blue-500 to-indigo-500'
      },
      {
        icon: CreditCard,
        title: 'F&I Workflow Automation',
        description: 'Digital finance & insurance desk with lender integrations, e-contracting, compliance checks, and product menu selling to maximise back-end profit.',
        metrics: '£480 average gross uplift per deal',
        color: 'from-emerald-500 to-green-500'
      },
      {
        icon: Wrench,
        title: 'Service & Parts Operations',
        description: 'Service bay scheduling, RO management, multi-point inspections, parts ordering, and warranty claim automation in one connected platform.',
        metrics: '35% increase in service throughput',
        color: 'from-amber-500 to-orange-500'
      },
      {
        icon: Calculator,
        title: 'Trade-In & Appraisal Intelligence',
        description: 'AI-driven trade valuations with live market data, reconditioning cost forecasting, and instant offer generation for showroom and online customers.',
        metrics: '92% appraisal accuracy vs. auction value',
        color: 'from-purple-500 to-violet-500'
      },
      {
        icon: Globe,
        title: 'Digital Retailing & Online Sales',
        description: 'End-to-end online buying with payment calculators, reservation deposits, e-signature, and home delivery scheduling fully integrated with DMS.',
        metrics: '28% of sales completed online',
        color: 'from-teal-500 to-cyan-500'
      },
      {
        icon: Phone,
        title: 'Showroom & BDC Telephony',
        description: 'Integrated cloud telephony with call recording, missed-call rescue, and intelligent routing to sales, service, and parts BDC teams.',
        metrics: '95% call answer rate',
        color: 'from-sky-500 to-blue-500'
      },
      {
        icon: Clipboard,
        title: 'Multi-Point Vehicle Inspections',
        description: 'Digital VHC checklists with photo and video evidence, customer-approved upsells, and instant SMS authorisation for service work.',
        metrics: '£212 average VHC upsell value',
        color: 'from-lime-500 to-green-500'
      },
      {
        icon: TrendingUp,
        title: 'Lifecycle Marketing Campaigns',
        description: 'Targeted email, SMS, and social campaigns triggered by lifecycle events such as MOT due, finance maturity, and equity opportunities.',
        metrics: '5x ROI on lifecycle campaigns',
        color: 'from-fuchsia-500 to-pink-500'
      },
      {
        icon: BarChart3,
        title: 'Dealer Group Performance Analytics',
        description: 'Executive dashboards comparing rooftop, brand, and consultant performance with drill-down to deal-level profitability.',
        metrics: 'Real-time group P&L visibility',
        color: 'from-slate-500 to-gray-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Target,
        title: 'AI Sales & Pricing Intelligence',
        description: 'Machine learning pricing engine analyses competitor stock, days-to-sell, and demand signals to recommend optimal list price by VIN.',
        metrics: '12% gross margin improvement',
        color: 'from-rose-500 to-pink-500'
      },
      {
        icon: MessageSquare,
        title: 'Omnichannel Customer Communication',
        description: 'Unified SMS, email, WhatsApp, and AI voice agents with full conversation history attached to every deal and service record.',
        metrics: '<2 min average response time',
        color: 'from-indigo-500 to-purple-500'
      },
      {
        icon: BarChart3,
        title: 'Manufacturer Compliance Reporting',
        description: 'Automated OEM reporting, programme tracking, CSI scoring, and incentive reconciliation across multiple franchise brands.',
        metrics: '100% on-time OEM submissions',
        color: 'from-blue-500 to-cyan-500'
      },
      {
        icon: Truck,
        title: 'Fleet & Commercial Sales',
        description: 'Dedicated fleet quoting, bulk deal structuring, lifecycle management, and B2B portal for corporate buyers and leasing partners.',
        metrics: '5x faster fleet quote turnaround',
        color: 'from-green-500 to-teal-500'
      },
      {
        icon: Settings,
        title: 'DMS & OEM Integrations',
        description: 'Pre-built connectors for CDK, Reynolds, Pinewood, Keyloop, and major OEM systems with bi-directional real-time sync.',
        metrics: '50+ certified integrations',
        color: 'from-orange-500 to-red-500'
      },
      {
        icon: Activity,
        title: 'Predictive Service Retention',
        description: 'AI predicts customer defection risk and triggers personalised retention campaigns, MOT reminders, and loyalty offers automatically.',
        metrics: '+22 point retention improvement',
        color: 'from-purple-500 to-indigo-500'
      },
      {
        icon: Search,
        title: 'AI Image & Damage Recognition',
        description: 'Computer-vision damage detection from photos to accelerate appraisals, body-shop estimates, and insurance claim assessments.',
        metrics: '85% faster damage assessment',
        color: 'from-cyan-500 to-blue-500'
      },
      {
        icon: AlertTriangle,
        title: 'Recall & Safety Campaign Tracking',
        description: 'Automated VIN-level recall tracking with customer outreach workflows and completion reporting to manufacturers.',
        metrics: '98% recall completion rate',
        color: 'from-red-500 to-orange-500'
      },
      {
        icon: DollarSign,
        title: 'Accounts & DOC Posting Automation',
        description: 'Automated daily operating control postings, deal funding reconciliation, and OEM rebate accruals into your accounting ledger.',
        metrics: '8 hours/week saved per accountant',
        color: 'from-indigo-500 to-blue-500'
      },
      {
        icon: Package,
        title: 'Parts e-Commerce & Wholesale',
        description: 'Trade parts portal, online retail catalogue with VIN lookup, click-and-collect, and courier integration for wholesale customers.',
        metrics: '+18% parts revenue growth',
        color: 'from-amber-500 to-yellow-500'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Automotive Compliance Framework',
        description: 'FCA, GDPR, Consumer Duty, and FSQS compliant with audit trails for every finance application and customer interaction.',
        metrics: '100% FCA Consumer Duty alignment',
        color: 'from-blue-600 to-indigo-600'
      },
      {
        icon: Lock,
        title: 'Customer & Finance Data Protection',
        description: 'PCI-DSS Level 1 secure storage of payment and finance data with field-level encryption and tokenised lender submissions.',
        metrics: 'PCI-DSS Level 1 certified',
        color: 'from-red-600 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Multi-Site Infrastructure',
        description: 'Enterprise cloud platform supporting unlimited rooftops, brands, and locations with sub-second sync across the dealer group.',
        metrics: '99.99% uptime across all sites',
        color: 'from-purple-600 to-violet-600'
      },
      {
        icon: CheckCircle,
        title: 'Role-Based Access Control',
        description: 'Granular permissions for sales, F&I, service, parts, and management with department-level dashboards and approval workflows.',
        metrics: '40+ pre-configured role templates',
        color: 'from-teal-600 to-cyan-600'
      },
      {
        icon: Zap,
        title: 'High-Performance DMS Sync',
        description: 'Real-time inventory, deal, and accounting sync with sub-100ms latency between showroom, service drive, and back-office.',
        metrics: '<100ms data propagation',
        color: 'from-orange-600 to-red-600'
      },
      {
        icon: Clock,
        title: '24/7 Monitoring & Support',
        description: 'UK-based automotive specialists with 24/7 monitoring, dedicated account management, and 3-year support included.',
        metrics: '15-minute critical response SLA',
        color: 'from-violet-600 to-purple-600'
      },
      {
        icon: AlertTriangle,
        title: 'Fraud & Identity Verification',
        description: 'Built-in identity verification, driving licence checks, and AML screening on every finance application and high-value transaction.',
        metrics: '99.4% fraud detection rate',
        color: 'from-rose-700 to-red-700'
      },
      {
        icon: FileText,
        title: 'Immutable Deal Audit Trails',
        description: 'Every quote, contract revision, signature, and approval is timestamped and tamper-evident for FCA and DPA evidence.',
        metrics: '7-year regulator-grade retention',
        color: 'from-amber-700 to-orange-700'
      },
      {
        icon: Globe,
        title: 'UK & EU Data Residency',
        description: 'Customer, finance, and CCTV data hosted exclusively in UK ISO 27001 facilities with optional EU residency for cross-border groups.',
        metrics: 'ISO 27001 + Cyber Essentials Plus',
        color: 'from-green-700 to-emerald-700'
      },
      {
        icon: Settings,
        title: 'Disaster Recovery & Backups',
        description: 'Continuous encrypted backups with 15-minute RPO and 1-hour RTO across geographically separated UK data centres.',
        metrics: 'RPO 15 min / RTO 1 hour',
        color: 'from-slate-700 to-gray-700'
      }
    ]
  },


  'enterprise-print-manufacturing': {
    keyBenefits: [
      {
        icon: Settings,
        title: 'Manufacturing Process Automation',
        description: 'End-to-end production workflow automation with intelligent job scheduling, resource optimization, and quality control.',
        metrics: '40% increase in production efficiency',
        color: 'from-blue-500 to-indigo-500'
      },
      {
        icon: BarChart3,
        title: 'Production Capacity Management',
        description: 'Real-time capacity planning, bottleneck identification, and dynamic resource allocation for optimal throughput.',
        metrics: '35% improvement in capacity utilization',
        color: 'from-green-500 to-teal-500'
      },
      {
        icon: CheckCircle,
        title: 'Quality Control & Assurance',
        description: 'Automated quality inspections, defect tracking, and statistical process control with real-time alerts.',
        metrics: '60% reduction in defect rates',
        color: 'from-purple-500 to-violet-500'
      },
      {
        icon: Database,
        title: 'Manufacturing Data Analytics',
        description: 'Comprehensive production analytics with OEE tracking, performance metrics, and predictive maintenance insights.',
        metrics: '25% increase in OEE scores',
        color: 'from-orange-500 to-red-500'
      },
      {
        icon: Globe,
        title: 'API Integration Platform',
        description: 'Seamless integration with ERP systems, MES platforms, and IoT devices for unified manufacturing operations.',
        metrics: '150+ manufacturing system integrations',
        color: 'from-teal-500 to-cyan-500'
      },
      {
        icon: TrendingUp,
        title: 'Cost Optimization & ROI',
        description: 'Advanced cost tracking, waste reduction analytics, and ROI optimization tools for manufacturing excellence.',
        metrics: '30% reduction in manufacturing costs',
        color: 'from-rose-500 to-pink-500'
      }
    ],
    advancedCapabilities: [
      {
        icon: Target,
        title: 'AI-Driven Production Planning',
        description: 'Machine learning algorithms optimize production schedules, material requirements, and resource allocation.',
        metrics: '95% schedule optimization accuracy',
        color: 'from-indigo-500 to-purple-500'
      },
      {
        icon: Activity,
        title: 'Predictive Maintenance System',
        description: 'IoT-enabled predictive maintenance with equipment health monitoring and automated maintenance scheduling.',
        metrics: '50% reduction in unplanned downtime',
        color: 'from-green-500 to-emerald-500'
      },
      {
        icon: Zap,
        title: 'Real-Time Manufacturing Intelligence',
        description: 'Live production dashboards, performance monitoring, and automated reporting for manufacturing operations.',
        metrics: 'Process 10K+ data points per second',
        color: 'from-blue-500 to-cyan-500'
      },
      {
        icon: Shield,
        title: 'Digital Twin Technology',
        description: 'Virtual manufacturing environment for process simulation, optimization, and scenario planning.',
        metrics: '85% simulation accuracy',
        color: 'from-purple-500 to-indigo-500'
      },
      {
        icon: FileText,
        title: 'Supply Chain Optimization',
        description: 'AI-powered supply chain management with demand forecasting, inventory optimization, and supplier analytics.',
        metrics: '40% inventory reduction',
        color: 'from-orange-500 to-yellow-500'
      },
      {
        icon: Users,
        title: 'Workforce Analytics & Optimization',
        description: 'Employee performance tracking, skill gap analysis, and workforce optimization for manufacturing teams.',
        metrics: '30% improvement in workforce productivity',
        color: 'from-teal-500 to-green-500'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with regular third-party security audits and certifications.',
        metrics: '99.9% compliance uptime',
        color: 'from-blue-600 to-indigo-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat detection and analysis with ML-powered anomaly detection and automated incident response.',
        metrics: '<5ms threat detection',
        color: 'from-green-600 to-emerald-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling, load balancing, and 99.99% uptime guarantee.',
        metrics: '10M+ secure transactions daily',
        color: 'from-purple-600 to-violet-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered firewall with advanced DDoS protection, rate limiting, and geo-blocking capabilities.',
        metrics: 'Blocks 99.9% of malicious traffic',
        color: 'from-orange-600 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO, biometric authentication, hardware tokens, and adaptive authentication policies.',
        metrics: '99.95% authentication success rate',
        color: 'from-teal-600 to-cyan-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning, behavioral analysis, and automated threat response capabilities.',
        metrics: 'Monitor 1B+ security events daily',
        color: 'from-indigo-600 to-blue-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated DLP policies, encrypted backups, point-in-time recovery, and cross-region data replication.',
        metrics: '15-minute recovery point objective',
        color: 'from-red-600 to-pink-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Zero-trust network architecture with micro-segmentation, continuous monitoring, and network analytics.',
        metrics: '100% network traffic visibility',
        color: 'from-emerald-600 to-green-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: '24/7 vulnerability scanning, penetration testing, security assessments, and automated patch management.',
        metrics: 'Sub-hour vulnerability detection',
        color: 'from-violet-600 to-purple-600'
      }
    ]
  },

  'manufacturing-quality-management': {
    keyBenefits: [
      {
        icon: CheckCircle,
        title: 'Quality Control Process Management',
        description: 'Comprehensive quality management with automated inspections, statistical process control, and real-time quality metrics.',
        metrics: '65% reduction in quality defects',
        color: 'from-green-500 to-emerald-500'
      },
      {
        icon: BarChart3,
        title: 'Statistical Process Control',
        description: 'Advanced SPC tools with control charts, capability analysis, and automated quality alerts for process optimization.',
        metrics: '40% improvement in process capability',
        color: 'from-blue-500 to-indigo-500'
      },
      {
        icon: AlertTriangle,
        title: 'Defect Tracking & Root Cause Analysis',
        description: 'Comprehensive defect tracking with automated root cause analysis, corrective action management, and trend analysis.',
        metrics: '50% faster defect resolution',
        color: 'from-orange-500 to-red-500'
      },
      {
        icon: Shield,
        title: 'Regulatory Compliance Management',
        description: 'Automated compliance tracking for ISO 9001, FDA, and industry-specific quality standards with audit trail management.',
        metrics: '99.8% compliance rate',
        color: 'from-purple-500 to-violet-500'
      },
      {
        icon: FileText,
        title: 'Quality Documentation System',
        description: 'Digital quality documentation with version control, electronic signatures, and automated report generation.',
        metrics: '70% reduction in documentation time',
        color: 'from-teal-500 to-cyan-500'
      },
      {
        icon: TrendingUp,
        title: 'Continuous Improvement Analytics',
        description: 'Quality performance analytics with improvement tracking, cost of quality analysis, and ROI measurement.',
        metrics: '35% improvement in quality ROI',
        color: 'from-rose-500 to-pink-500'
      }
    ],
    advancedCapabilities: [
      {
        icon: Target,
        title: 'AI-Powered Quality Prediction',
        description: 'Machine learning algorithms predict quality issues before they occur, enabling proactive quality management.',
        metrics: '90% quality issue prediction accuracy',
        color: 'from-indigo-500 to-purple-500'
      },
      {
        icon: Activity,
        title: 'Real-Time Quality Monitoring',
        description: 'IoT-enabled real-time quality monitoring with automated alerts and corrective action triggers.',
        metrics: 'Monitor 1000+ quality parameters',
        color: 'from-green-500 to-teal-500'
      },
      {
        icon: Database,
        title: 'Quality Data Intelligence Platform',
        description: 'Advanced analytics platform aggregating quality data across multiple production lines and facilities.',
        metrics: 'Process 5M+ quality data points daily',
        color: 'from-blue-500 to-cyan-500'
      },
      {
        icon: Globe,
        title: 'Supplier Quality Management',
        description: 'Comprehensive supplier quality tracking with performance scorecards, audit management, and collaboration tools.',
        metrics: '45% improvement in supplier quality',
        color: 'from-orange-500 to-yellow-500'
      },
      {
        icon: Settings,
        title: 'Quality System Integration',
        description: 'Seamless integration with ERP, MES, and LIMS systems for unified quality management across the enterprise.',
        metrics: '100+ quality system integrations',
        color: 'from-purple-500 to-indigo-500'
      },
      {
        icon: Users,
        title: 'Quality Training & Competency',
        description: 'Automated quality training programs, competency tracking, and certification management for quality teams.',
        metrics: '60% improvement in quality training efficiency',
        color: 'from-teal-500 to-green-500'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with regular third-party security audits and certifications.',
        metrics: '99.9% compliance uptime',
        color: 'from-blue-600 to-indigo-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat detection and analysis with ML-powered anomaly detection and automated incident response.',
        metrics: '<5ms threat detection',
        color: 'from-green-600 to-emerald-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling, load balancing, and 99.99% uptime guarantee.',
        metrics: '10M+ secure transactions daily',
        color: 'from-purple-600 to-violet-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered firewall with advanced DDoS protection, rate limiting, and geo-blocking capabilities.',
        metrics: 'Blocks 99.9% of malicious traffic',
        color: 'from-orange-600 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO, biometric authentication, hardware tokens, and adaptive authentication policies.',
        metrics: '99.95% authentication success rate',
        color: 'from-teal-600 to-cyan-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning, behavioral analysis, and automated threat response capabilities.',
        metrics: 'Monitor 1B+ security events daily',
        color: 'from-indigo-600 to-blue-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated DLP policies, encrypted backups, point-in-time recovery, and cross-region data replication.',
        metrics: '15-minute recovery point objective',
        color: 'from-red-600 to-pink-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Zero-trust network architecture with micro-segmentation, continuous monitoring, and network analytics.',
        metrics: '100% network traffic visibility',
        color: 'from-emerald-600 to-green-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: '24/7 vulnerability scanning, penetration testing, security assessments, and automated patch management.',
        metrics: 'Sub-hour vulnerability detection',
        color: 'from-violet-600 to-purple-600'
      }
    ]
  },

  'healthcare-compliance-management': {
    keyBenefits: [
      {
        icon: Stethoscope,
        title: 'HIPAA Compliance Automation',
        description: 'Automated HIPAA compliance monitoring, risk assessment, and audit trail management with real-time alerts.',
        metrics: '99.9% HIPAA compliance rate',
        color: 'from-blue-500 to-indigo-500'
      },
      {
        icon: Shield,
        title: 'Healthcare Risk Assessment',
        description: 'Comprehensive risk assessment tools with vulnerability scanning, threat analysis, and mitigation planning.',
        metrics: '60% reduction in compliance risks',
        color: 'from-green-500 to-emerald-500'
      },
      {
        icon: FileText,
        title: 'Audit Trail & Documentation',
        description: 'Complete audit trail management with automated documentation, compliance reporting, and evidence collection.',
        metrics: '75% faster audit preparation',
        color: 'from-purple-500 to-violet-500'
      },
      {
        icon: AlertTriangle,
        title: 'Incident Management System',
        description: 'Healthcare incident tracking, breach notification management, and regulatory reporting automation.',
        metrics: '50% faster incident response',
        color: 'from-orange-500 to-red-500'
      },
      {
        icon: Users,
        title: 'Staff Training & Certification',
        description: 'Automated compliance training programs, certification tracking, and competency management for healthcare staff.',
        metrics: '80% improvement in training compliance',
        color: 'from-teal-500 to-cyan-500'
      },
      {
        icon: BarChart3,
        title: 'Compliance Analytics & Reporting',
        description: 'Advanced compliance analytics with performance metrics, trend analysis, and regulatory reporting dashboards.',
        metrics: '90% reduction in manual reporting',
        color: 'from-rose-500 to-pink-500'
      }
    ],
    advancedCapabilities: [
      {
        icon: Target,
        title: 'AI-Powered Compliance Monitoring',
        description: 'Machine learning algorithms continuously monitor compliance status and predict potential violations.',
        metrics: '95% violation prediction accuracy',
        color: 'from-indigo-500 to-purple-500'
      },
      {
        icon: Database,
        title: 'Healthcare Data Governance',
        description: 'Comprehensive data governance platform with PHI protection, access controls, and data lineage tracking.',
        metrics: 'Secure 10M+ patient records',
        color: 'from-blue-500 to-cyan-500'
      },
      {
        icon: Globe,
        title: 'Multi-Regulatory Compliance',
        description: 'Support for HIPAA, HITECH, FDA, CMS, and international healthcare regulations with automated updates.',
        metrics: '25+ healthcare regulations supported',
        color: 'from-green-500 to-teal-500'
      },
      {
        icon: Activity,
        title: 'Real-Time Compliance Monitoring',
        description: 'Continuous compliance monitoring with real-time alerts, automated remediation, and exception management.',
        metrics: 'Monitor 1000+ compliance controls',
        color: 'from-purple-500 to-indigo-500'
      },
      {
        icon: Settings,
        title: 'Healthcare System Integration',
        description: 'Seamless integration with EHR systems, practice management software, and healthcare information exchanges.',
        metrics: '200+ healthcare system integrations',
        color: 'from-orange-500 to-yellow-500'
      },
      {
        icon: Lock,
        title: 'Advanced PHI Protection',
        description: 'Multi-layered PHI protection with encryption, tokenization, and privacy-preserving analytics.',
        metrics: '99.99% PHI protection rate',
        color: 'from-teal-500 to-green-500'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with regular third-party security audits and certifications.',
        metrics: '99.9% compliance uptime',
        color: 'from-blue-600 to-indigo-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat detection and analysis with ML-powered anomaly detection and automated incident response.',
        metrics: '<5ms threat detection',
        color: 'from-green-600 to-emerald-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling, load balancing, and 99.99% uptime guarantee.',
        metrics: '10M+ secure transactions daily',
        color: 'from-purple-600 to-violet-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered firewall with advanced DDoS protection, rate limiting, and geo-blocking capabilities.',
        metrics: 'Blocks 99.9% of malicious traffic',
        color: 'from-orange-600 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO, biometric authentication, hardware tokens, and adaptive authentication policies.',
        metrics: '99.95% authentication success rate',
        color: 'from-teal-600 to-cyan-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning, behavioral analysis, and automated threat response capabilities.',
        metrics: 'Monitor 1B+ security events daily',
        color: 'from-indigo-600 to-blue-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated DLP policies, encrypted backups, point-in-time recovery, and cross-region data replication.',
        metrics: '15-minute recovery point objective',
        color: 'from-red-600 to-pink-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Zero-trust network architecture with micro-segmentation, continuous monitoring, and network analytics.',
        metrics: '100% network traffic visibility',
        color: 'from-emerald-600 to-green-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: '24/7 vulnerability scanning, penetration testing, security assessments, and automated patch management.',
        metrics: 'Sub-hour vulnerability detection',
        color: 'from-violet-600 to-purple-600'
      }
    ]
  },

  'legal-document-analytics': {
    keyBenefits: [
      {
        icon: Scale,
        title: 'Contract Analysis & Review',
        description: 'AI-powered contract analysis with clause extraction, risk identification, and automated redlining capabilities.',
        metrics: '80% faster contract review',
        color: 'from-blue-500 to-indigo-500'
      },
      {
        icon: Search,
        title: 'Legal Research & Discovery',
        description: 'Advanced legal research tools with case law analysis, precedent identification, and automated citation checking.',
        metrics: '70% reduction in research time',
        color: 'from-green-500 to-emerald-500'
      },
      {
        icon: AlertTriangle,
        title: 'Risk Identification & Assessment',
        description: 'Comprehensive risk analysis with clause-level risk scoring, regulatory compliance checks, and mitigation recommendations.',
        metrics: '90% risk identification accuracy',
        color: 'from-orange-500 to-red-500'
      },
      {
        icon: FileText,
        title: 'Document Management & Storage',
        description: 'Secure document repository with version control, search capabilities, and automated organization systems.',
        metrics: '95% document retrieval accuracy',
        color: 'from-purple-500 to-violet-500'
      },
      {
        icon: TrendingUp,
        title: 'Legal Analytics & Insights',
        description: 'Advanced analytics for contract performance, negotiation patterns, and legal outcome predictions.',
        metrics: '85% outcome prediction accuracy',
        color: 'from-teal-500 to-cyan-500'
      },
      {
        icon: Users,
        title: 'Collaboration & Workflow',
        description: 'Team collaboration tools with workflow automation, task management, and real-time document sharing.',
        metrics: '60% improvement in team efficiency',
        color: 'from-rose-500 to-pink-500'
      }
    ],
    advancedCapabilities: [
      {
        icon: Target,
        title: 'AI Legal Language Processing',
        description: 'Advanced NLP for legal document understanding, entity extraction, and semantic analysis of complex legal text.',
        metrics: '98% legal text understanding accuracy',
        color: 'from-indigo-500 to-purple-500'
      },
      {
        icon: Database,
        title: 'Legal Knowledge Graph',
        description: 'Comprehensive legal knowledge graph with relationships between cases, statutes, regulations, and precedents.',
        metrics: '50M+ legal entity relationships',
        color: 'from-blue-500 to-cyan-500'
      },
      {
        icon: Globe,
        title: 'Multi-Jurisdiction Compliance',
        description: 'Support for multiple legal jurisdictions with automated compliance checking and regulatory updates.',
        metrics: '100+ jurisdictions supported',
        color: 'from-green-500 to-teal-500'
      },
      {
        icon: Activity,
        title: 'Real-Time Legal Monitoring',
        description: 'Continuous monitoring of legal changes, regulatory updates, and relevant case law developments.',
        metrics: 'Monitor 10K+ legal sources daily',
        color: 'from-purple-500 to-indigo-500'
      },
      {
        icon: Settings,
        title: 'Legal System Integration',
        description: 'Seamless integration with practice management systems, e-discovery platforms, and court filing systems.',
        metrics: '150+ legal system integrations',
        color: 'from-orange-500 to-yellow-500'
      },
      {
        icon: CheckCircle,
        title: 'Automated Compliance Checking',
        description: 'AI-powered compliance verification with automated alerts for regulatory changes and deadline management.',
        metrics: '99.5% compliance accuracy',
        color: 'from-teal-500 to-green-500'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with regular third-party security audits and certifications.',
        metrics: '99.9% compliance uptime',
        color: 'from-blue-600 to-indigo-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat detection and analysis with ML-powered anomaly detection and automated incident response.',
        metrics: '<5ms threat detection',
        color: 'from-green-600 to-emerald-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling, load balancing, and 99.99% uptime guarantee.',
        metrics: '10M+ secure transactions daily',
        color: 'from-purple-600 to-violet-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered firewall with advanced DDoS protection, rate limiting, and geo-blocking capabilities.',
        metrics: 'Blocks 99.9% of malicious traffic',
        color: 'from-orange-600 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO, biometric authentication, hardware tokens, and adaptive authentication policies.',
        metrics: '99.95% authentication success rate',
        color: 'from-teal-600 to-cyan-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning, behavioral analysis, and automated threat response capabilities.',
        metrics: 'Monitor 1B+ security events daily',
        color: 'from-indigo-600 to-blue-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated DLP policies, encrypted backups, point-in-time recovery, and cross-region data replication.',
        metrics: '15-minute recovery point objective',
        color: 'from-red-600 to-pink-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Zero-trust network architecture with micro-segmentation, continuous monitoring, and network analytics.',
        metrics: '100% network traffic visibility',
        color: 'from-emerald-600 to-green-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: '24/7 vulnerability scanning, penetration testing, security assessments, and automated patch management.',
        metrics: 'Sub-hour vulnerability detection',
        color: 'from-violet-600 to-purple-600'
      }
    ]
  },

  'retail-assortment-planning': {
    keyBenefits: [
      {
        icon: ShoppingCart,
        title: 'Demand Forecasting & Planning',
        description: 'AI-powered demand forecasting with seasonal analysis, trend prediction, and automated replenishment planning.',
        metrics: '85% forecast accuracy improvement',
        color: 'from-blue-500 to-indigo-500'
      },
      {
        icon: Package,
        title: 'Inventory Optimization',
        description: 'Advanced inventory management with stock level optimization, turnover analysis, and automated ordering systems.',
        metrics: '40% reduction in excess inventory',
        color: 'from-green-500 to-emerald-500'
      },
      {
        icon: TrendingUp,
        title: 'Sales Performance Analytics',
        description: 'Comprehensive sales analytics with category performance, product profitability, and market trend analysis.',
        metrics: '30% increase in category profits',
        color: 'from-purple-500 to-violet-500'
      },
      {
        icon: Target,
        title: 'Margin Analysis & Optimization',
        description: 'Detailed margin analysis with pricing optimization, promotional planning, and profitability tracking.',
        metrics: '25% improvement in gross margins',
        color: 'from-orange-500 to-red-500'
      },
      {
        icon: Users,
        title: 'Customer Behavior Insights',
        description: 'Advanced customer analytics with shopping pattern analysis, preference identification, and personalization.',
        metrics: '50% better customer targeting',
        color: 'from-teal-500 to-cyan-500'
      },
      {
        icon: Globe,
        title: 'Multi-Channel Assortment',
        description: 'Unified assortment planning across online, retail, and mobile channels with synchronized inventory management.',
        metrics: '35% improvement in channel efficiency',
        color: 'from-rose-500 to-pink-500'
      }
    ],
    advancedCapabilities: [
      {
        icon: Activity,
        title: 'Real-Time Market Intelligence',
        description: 'Live market data analysis with competitor monitoring, price tracking, and trend identification.',
        metrics: 'Monitor 1M+ products daily',
        color: 'from-indigo-500 to-purple-500'
      },
      {
        icon: Database,
        title: 'Retail Data Analytics Platform',
        description: 'Comprehensive retail analytics with sales data aggregation, customer insights, and performance benchmarking.',
        metrics: 'Process 100M+ transactions daily',
        color: 'from-blue-500 to-cyan-500'
      },
      {
        icon: Settings,
        title: 'Dynamic Pricing & Promotions',
        description: 'AI-driven pricing optimization with promotional planning, markdown management, and competitive pricing.',
        metrics: '20% increase in pricing efficiency',
        color: 'from-green-500 to-teal-500'
      },
      {
        icon: Zap,
        title: 'Automated Assortment Optimization',
        description: 'Machine learning algorithms automatically optimize product assortments based on performance and trends.',
        metrics: '90% assortment optimization accuracy',
        color: 'from-purple-500 to-indigo-500'
      },
      {
        icon: BarChart3,
        title: 'Supply Chain Integration',
        description: 'Seamless integration with suppliers, distributors, and logistics providers for optimized supply chain management.',
        metrics: '300+ supply chain integrations',
        color: 'from-orange-500 to-yellow-500'
      },
      {
        icon: CheckCircle,
        title: 'Seasonal & Trend Planning',
        description: 'Advanced seasonal planning with trend analysis, fashion forecasting, and lifecycle management.',
        metrics: '75% seasonal planning accuracy',
        color: 'from-teal-500 to-green-500'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with regular third-party security audits and certifications.',
        metrics: '99.9% compliance uptime',
        color: 'from-blue-600 to-indigo-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat detection and analysis with ML-powered anomaly detection and automated incident response.',
        metrics: '<5ms threat detection',
        color: 'from-green-600 to-emerald-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling, load balancing, and 99.99% uptime guarantee.',
        metrics: '10M+ secure transactions daily',
        color: 'from-purple-600 to-violet-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered firewall with advanced DDoS protection, rate limiting, and geo-blocking capabilities.',
        metrics: 'Blocks 99.9% of malicious traffic',
        color: 'from-orange-600 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO, biometric authentication, hardware tokens, and adaptive authentication policies.',
        metrics: '99.95% authentication success rate',
        color: 'from-teal-600 to-cyan-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning, behavioral analysis, and automated threat response capabilities.',
        metrics: 'Monitor 1B+ security events daily',
        color: 'from-indigo-600 to-blue-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated DLP policies, encrypted backups, point-in-time recovery, and cross-region data replication.',
        metrics: '15-minute recovery point objective',
        color: 'from-red-600 to-pink-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Zero-trust network architecture with micro-segmentation, continuous monitoring, and network analytics.',
        metrics: '100% network traffic visibility',
        color: 'from-emerald-600 to-green-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: '24/7 vulnerability scanning, penetration testing, security assessments, and automated patch management.',
        metrics: 'Sub-hour vulnerability detection',
        color: 'from-violet-600 to-purple-600'
      }
    ]
  },

  'restaurant-inventory-management': {
    keyBenefits: [
      {
        icon: ChefHat,
        title: 'Food Inventory & Cost Control',
        description: 'Comprehensive food inventory management with cost tracking, waste reduction, and automated ordering systems.',
        metrics: '35% reduction in food costs',
        color: 'from-orange-500 to-red-500'
      },
      {
        icon: Package,
        title: 'Waste Reduction & Sustainability',
        description: 'Advanced waste tracking with expiration monitoring, portion control, and sustainability metrics.',
        metrics: '50% reduction in food waste',
        color: 'from-green-500 to-emerald-500'
      },
      {
        icon: BarChart3,
        title: 'Menu Engineering Analytics',
        description: 'Menu performance analysis with profitability tracking, popularity metrics, and pricing optimization.',
        metrics: '25% improvement in menu profitability',
        color: 'from-blue-500 to-indigo-500'
      },
      {
        icon: Truck,
        title: 'Supplier Management & Integration',
        description: 'Streamlined supplier relationships with automated ordering, price comparison, and quality tracking.',
        metrics: '40% efficiency in supplier management',
        color: 'from-purple-500 to-violet-500'
      },
      {
        icon: Calculator,
        title: 'Recipe Costing & Standardization',
        description: 'Detailed recipe costing with ingredient tracking, portion control, and standardized preparation methods.',
        metrics: '30% improvement in recipe consistency',
        color: 'from-teal-500 to-cyan-500'
      },
      {
        icon: TrendingUp,
        title: 'Financial Performance Insights',
        description: 'Comprehensive financial analytics with profit margin analysis, cost center tracking, and ROI measurement.',
        metrics: '20% increase in profit margins',
        color: 'from-rose-500 to-pink-500'
      }
    ],
    advancedCapabilities: [
      {
        icon: Target,
        title: 'AI Demand Forecasting',
        description: 'Machine learning algorithms predict demand patterns based on historical data, weather, events, and trends.',
        metrics: '90% demand prediction accuracy',
        color: 'from-indigo-500 to-purple-500'
      },
      {
        icon: Activity,
        title: 'Real-Time Inventory Tracking',
        description: 'IoT-enabled real-time inventory monitoring with automated alerts and restocking recommendations.',
        metrics: 'Track 10K+ inventory items',
        color: 'from-green-500 to-teal-500'
      },
      {
        icon: Database,
        title: 'Restaurant Intelligence Platform',
        description: 'Comprehensive restaurant analytics with sales data, customer preferences, and operational insights.',
        metrics: 'Process 1M+ restaurant transactions',
        color: 'from-blue-500 to-cyan-500'
      },
      {
        icon: Globe,
        title: 'Multi-Location Management',
        description: 'Centralized management for restaurant chains with location-specific analytics and standardized operations.',
        metrics: 'Manage 500+ restaurant locations',
        color: 'from-orange-500 to-yellow-500'
      },
      {
        icon: Settings,
        title: 'POS & Kitchen Integration',
        description: 'Seamless integration with POS systems, kitchen displays, and restaurant management platforms.',
        metrics: '200+ restaurant system integrations',
        color: 'from-purple-500 to-indigo-500'
      },
      {
        icon: CheckCircle,
        title: 'Food Safety & Compliance',
        description: 'Automated food safety monitoring with temperature tracking, HACCP compliance, and audit management.',
        metrics: '99.8% food safety compliance',
        color: 'from-teal-500 to-green-500'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with regular third-party security audits and certifications.',
        metrics: '99.9% compliance uptime',
        color: 'from-blue-600 to-indigo-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat detection and analysis with ML-powered anomaly detection and automated incident response.',
        metrics: '<5ms threat detection',
        color: 'from-green-600 to-emerald-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling, load balancing, and 99.99% uptime guarantee.',
        metrics: '10M+ secure transactions daily',
        color: 'from-purple-600 to-violet-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered firewall with advanced DDoS protection, rate limiting, and geo-blocking capabilities.',
        metrics: 'Blocks 99.9% of malicious traffic',
        color: 'from-orange-600 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO, biometric authentication, hardware tokens, and adaptive authentication policies.',
        metrics: '99.95% authentication success rate',
        color: 'from-teal-600 to-cyan-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning, behavioral analysis, and automated threat response capabilities.',
        metrics: 'Monitor 1B+ security events daily',
        color: 'from-indigo-600 to-blue-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated DLP policies, encrypted backups, point-in-time recovery, and cross-region data replication.',
        metrics: '15-minute recovery point objective',
        color: 'from-red-600 to-pink-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Zero-trust network architecture with micro-segmentation, continuous monitoring, and network analytics.',
        metrics: '100% network traffic visibility',
        color: 'from-emerald-600 to-green-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: '24/7 vulnerability scanning, penetration testing, security assessments, and automated patch management.',
        metrics: 'Sub-hour vulnerability detection',
        color: 'from-violet-600 to-purple-600'
      }
    ]
  },

  'hotel-revenue-management': {
    keyBenefits: [
      {
        icon: Bed,
        title: 'Dynamic Pricing & Revenue Optimization',
        description: 'AI-powered dynamic pricing with demand forecasting, competitor analysis, and revenue maximization strategies.',
        metrics: '35% increase in revenue per room',
        color: 'from-blue-500 to-indigo-500'
      },
      {
        icon: TrendingUp,
        title: 'Occupancy & Demand Forecasting',
        description: 'Advanced demand forecasting with seasonal analysis, event impact assessment, and booking pattern recognition.',
        metrics: '90% demand prediction accuracy',
        color: 'from-green-500 to-emerald-500'
      },
      {
        icon: BarChart3,
        title: 'Competitor Monitoring & Analysis',
        description: 'Real-time competitor rate monitoring with market positioning analysis and pricing strategy optimization.',
        metrics: 'Monitor 1000+ competitor properties',
        color: 'from-purple-500 to-violet-500'
      },
      {
        icon: Target,
        title: 'Channel Management & Distribution',
        description: 'Centralized channel management with rate parity monitoring, inventory distribution, and commission optimization.',
        metrics: '50% improvement in channel efficiency',
        color: 'from-orange-500 to-red-500'
      },
      {
        icon: DollarSign,
        title: 'Yield Management & Profitability',
        description: 'Comprehensive yield management with profit margin analysis, cost center tracking, and ROI optimization.',
        metrics: '25% increase in profit margins',
        color: 'from-teal-500 to-cyan-500'
      },
      {
        icon: Users,
        title: 'Guest Segmentation & Personalization',
        description: 'Advanced guest analytics with segmentation, preference tracking, and personalized pricing strategies.',
        metrics: '40% improvement in guest satisfaction',
        color: 'from-rose-500 to-pink-500'
      }
    ],
    advancedCapabilities: [
      {
        icon: Activity,
        title: 'Real-Time Market Intelligence',
        description: 'Live market data with booking trends, rate changes, and demand patterns across multiple markets.',
        metrics: 'Process 10M+ booking signals daily',
        color: 'from-indigo-500 to-purple-500'
      },
      {
        icon: Database,
        title: 'Hotel Analytics Platform',
        description: 'Comprehensive hotel performance analytics with benchmarking, KPI tracking, and predictive insights.',
        metrics: 'Analyze 500K+ hotel properties',
        color: 'from-blue-500 to-cyan-500'
      },
      {
        icon: Globe,
        title: 'Multi-Property Management',
        description: 'Centralized revenue management for hotel chains with property-specific strategies and group optimization.',
        metrics: 'Manage 1000+ hotel properties',
        color: 'from-green-500 to-teal-500'
      },
      {
        icon: Settings,
        title: 'PMS & Channel Integration',
        description: 'Seamless integration with property management systems, channel managers, and booking platforms.',
        metrics: '300+ hospitality system integrations',
        color: 'from-purple-500 to-indigo-500'
      },
      {
        icon: Zap,
        title: 'Automated Revenue Strategies',
        description: 'AI-driven automated pricing strategies with rule-based optimization and performance learning.',
        metrics: '95% strategy optimization accuracy',
        color: 'from-orange-500 to-yellow-500'
      },
      {
        icon: CheckCircle,
        title: 'Event & Market Impact Analysis',
        description: 'Advanced event tracking with market impact assessment, demand surge prediction, and pricing adjustments.',
        metrics: 'Track 50K+ market events',
        color: 'from-teal-500 to-green-500'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with regular third-party security audits and certifications.',
        metrics: '99.9% compliance uptime',
        color: 'from-blue-600 to-indigo-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat detection and analysis with ML-powered anomaly detection and automated incident response.',
        metrics: '<5ms threat detection',
        color: 'from-green-600 to-emerald-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling, load balancing, and 99.99% uptime guarantee.',
        metrics: '10M+ secure transactions daily',
        color: 'from-purple-600 to-violet-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered firewall with advanced DDoS protection, rate limiting, and geo-blocking capabilities.',
        metrics: 'Blocks 99.9% of malicious traffic',
        color: 'from-orange-600 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO, biometric authentication, hardware tokens, and adaptive authentication policies.',
        metrics: '99.95% authentication success rate',
        color: 'from-teal-600 to-cyan-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning, behavioral analysis, and automated threat response capabilities.',
        metrics: 'Monitor 1B+ security events daily',
        color: 'from-indigo-600 to-blue-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated DLP policies, encrypted backups, point-in-time recovery, and cross-region data replication.',
        metrics: '15-minute recovery point objective',
        color: 'from-red-600 to-pink-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Zero-trust network architecture with micro-segmentation, continuous monitoring, and network analytics.',
        metrics: '100% network traffic visibility',
        color: 'from-emerald-600 to-green-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: '24/7 vulnerability scanning, penetration testing, security assessments, and automated patch management.',
        metrics: 'Sub-hour vulnerability detection',
        color: 'from-violet-600 to-purple-600'
      }
    ]
  },

  'insurance-claims-automation': {
    keyBenefits: [
      {
        icon: FileText,
        title: 'Automated Claims Processing',
        description: 'End-to-end claims automation with intake processing, documentation review, and decision-making workflows.',
        metrics: '70% faster claims processing',
        color: 'from-blue-500 to-indigo-500'
      },
      {
        icon: Search,
        title: 'Damage Assessment & Evaluation',
        description: 'AI-powered damage assessment with photo analysis, cost estimation, and repair recommendation systems.',
        metrics: '90% assessment accuracy',
        color: 'from-green-500 to-emerald-500'
      },
      {
        icon: AlertTriangle,
        title: 'Fraud Detection & Prevention',
        description: 'Advanced fraud detection with pattern recognition, anomaly detection, and risk scoring algorithms.',
        metrics: '85% fraud detection accuracy',
        color: 'from-orange-500 to-red-500'
      },
      {
        icon: Calculator,
        title: 'Settlement Automation & Optimization',
        description: 'Automated settlement calculations with policy analysis, coverage verification, and payout optimization.',
        metrics: '60% reduction in settlement time',
        color: 'from-purple-500 to-violet-500'
      },
      {
        icon: Users,
        title: 'Customer Communication & Support',
        description: 'Automated customer communications with status updates, documentation requests, and support workflows.',
        metrics: '50% improvement in customer satisfaction',
        color: 'from-teal-500 to-cyan-500'
      },
      {
        icon: BarChart3,
        title: 'Claims Analytics & Reporting',
        description: 'Comprehensive claims analytics with performance metrics, trend analysis, and regulatory reporting.',
        metrics: '40% improvement in operational efficiency',
        color: 'from-rose-500 to-pink-500'
      }
    ],
    advancedCapabilities: [
      {
        icon: Target,
        title: 'AI Claims Intelligence',
        description: 'Machine learning models for claims categorization, severity assessment, and outcome prediction.',
        metrics: '95% claims categorization accuracy',
        color: 'from-indigo-500 to-purple-500'
      },
      {
        icon: Activity,
        title: 'Real-Time Claims Monitoring',
        description: 'Live claims tracking with automated alerts, escalation workflows, and performance monitoring.',
        metrics: 'Process 100K+ claims daily',
        color: 'from-green-500 to-teal-500'
      },
      {
        icon: Database,
        title: 'Insurance Data Analytics',
        description: 'Comprehensive insurance analytics with risk assessment, actuarial modeling, and predictive insights.',
        metrics: 'Analyze 50M+ insurance policies',
        color: 'from-blue-500 to-cyan-500'
      },
      {
        icon: Globe,
        title: 'Multi-Line Insurance Support',
        description: 'Support for auto, property, health, and commercial insurance with specialized workflows for each line.',
        metrics: '25+ insurance product lines',
        color: 'from-orange-500 to-yellow-500'
      },
      {
        icon: Settings,
        title: 'Insurance System Integration',
        description: 'Seamless integration with core insurance systems, policy management, and third-party service providers.',
        metrics: '200+ insurance system integrations',
        color: 'from-purple-500 to-indigo-500'
      },
      {
        icon: CheckCircle,
        title: 'Regulatory Compliance Automation',
        description: 'Automated compliance monitoring with regulatory reporting, audit trails, and policy adherence verification.',
        metrics: '99.5% regulatory compliance',
        color: 'from-teal-500 to-green-500'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with regular third-party security audits and certifications.',
        metrics: '99.9% compliance uptime',
        color: 'from-blue-600 to-indigo-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat detection and analysis with ML-powered anomaly detection and automated incident response.',
        metrics: '<5ms threat detection',
        color: 'from-green-600 to-emerald-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling, load balancing, and 99.99% uptime guarantee.',
        metrics: '10M+ secure transactions daily',
        color: 'from-purple-600 to-violet-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered firewall with advanced DDoS protection, rate limiting, and geo-blocking capabilities.',
        metrics: 'Blocks 99.9% of malicious traffic',
        color: 'from-orange-600 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO, biometric authentication, hardware tokens, and adaptive authentication policies.',
        metrics: '99.95% authentication success rate',
        color: 'from-teal-600 to-cyan-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning, behavioral analysis, and automated threat response capabilities.',
        metrics: 'Monitor 1B+ security events daily',
        color: 'from-indigo-600 to-blue-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated DLP policies, encrypted backups, point-in-time recovery, and cross-region data replication.',
        metrics: '15-minute recovery point objective',
        color: 'from-red-600 to-pink-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Zero-trust network architecture with micro-segmentation, continuous monitoring, and network analytics.',
        metrics: '100% network traffic visibility',
        color: 'from-emerald-600 to-green-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: '24/7 vulnerability scanning, penetration testing, security assessments, and automated patch management.',
        metrics: 'Sub-hour vulnerability detection',
        color: 'from-violet-600 to-purple-600'
      }
    ]
  }
};